import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Building2, Edit, Trash2, Eye, MapPin, Phone, Mail, User, Calendar, FileText, ArrowLeft, CheckCircle, Clock, Briefcase } from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Clients = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { selectedBusinessId } = useBusiness();
  
  // State
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [userRole, setUserRole] = useState("");
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientTasks, setClientTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    client_name: "",
    city: "",
    service_taken: "",
    contact_person_name: "",
    contact_email: "",
    contact_phone: "",
    notes: ""
  });

  const canManageClients = ["super_admin", "admin", "manager"].includes(userRole);

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setUserRole(data.role);
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };
    fetchUserRole();
  }, []);

  // Fetch clients
  const fetchClients = async () => {
    try {
      setLoading(true);
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/clients?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/clients`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      } else {
        toast.error("Failed to fetch clients");
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Error loading clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [selectedBusinessId]);

  // Handle URL parameter for client profile
  useEffect(() => {
    if (clientId && clients.length > 0) {
      const client = clients.find(c => c.client_id === clientId);
      if (client) {
        handleViewProfile(client);
      }
    }
  }, [clientId, clients]);

  // Fetch client tasks for profile
  const fetchClientTasks = async (clientId) => {
    try {
      setLoadingTasks(true);
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientId}/tasks`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setClientTasks(data);
      }
    } catch (error) {
      console.error("Error fetching client tasks:", error);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Filter clients by search
  const filteredClients = clients.filter(client => {
    const query = searchQuery.toLowerCase();
    return (
      client.client_name?.toLowerCase().includes(query) ||
      client.city?.toLowerCase().includes(query) ||
      client.contact_person_name?.toLowerCase().includes(query) ||
      client.service_taken?.toLowerCase().includes(query)
    );
  });

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      client_name: "",
      city: "",
      service_taken: "",
      contact_person_name: "",
      contact_email: "",
      contact_phone: "",
      notes: ""
    });
  };

  // Create client
  const handleCreateClient = async () => {
    if (!formData.client_name.trim()) {
      toast.error("Client name is required");
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Creating client with data:", formData);
      const response = await fetch(`${BACKEND_URL}/api/clients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      console.log("Response status:", response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log("Client created:", result);
        toast.success("Client created successfully");
        setShowAddDialog(false);
        resetForm();
        fetchClients();
      } else {
        const error = await response.json();
        console.error("Error response:", error);
        toast.error(error.detail || "Failed to create client");
      }
    } catch (error) {
      console.error("Error creating client:", error);
      toast.error("Error creating client: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update client
  const handleUpdateClient = async () => {
    if (!formData.client_name.trim()) {
      toast.error("Client name is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.client_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Client updated successfully");
        setShowEditDialog(false);
        resetForm();
        setSelectedClient(null);
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update client");
      }
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Error updating client");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete client
  const handleDeleteClient = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.client_id}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Client deleted successfully");
        setShowDeleteDialog(false);
        setSelectedClient(null);
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete client");
      }
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error("Error deleting client");
    }
  };

  // Open edit dialog
  const handleEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      client_name: client.client_name || "",
      city: client.city || "",
      service_taken: client.service_taken || "",
      contact_person_name: client.contact_person_name || "",
      contact_email: client.contact_email || "",
      contact_phone: client.contact_phone || "",
      notes: client.notes || ""
    });
    setShowEditDialog(true);
  };

  // Open delete dialog
  const handleDelete = (client) => {
    setSelectedClient(client);
    setShowDeleteDialog(true);
  };

  // View profile
  const handleViewProfile = (client) => {
    setSelectedClient(client);
    fetchClientTasks(client.client_id);
    setShowProfileDialog(true);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Building2 className="w-8 h-8 text-indigo-600" />
            Clients
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage your business clients and their profiles
          </p>
        </div>
        
        {canManageClients && (
          <Button 
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        )}
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="client-search-input"
        />
      </div>

      {/* Clients Table */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Building2 className="w-12 h-12 mb-4 opacity-50" />
              <p>{searchQuery ? "No clients match your search" : "No clients yet"}</p>
              {canManageClients && !searchQuery && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => { resetForm(); setShowAddDialog(true); }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add your first client
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="font-semibold">#</TableHead>
                    <TableHead className="font-semibold">Client Name</TableHead>
                    <TableHead className="font-semibold">City</TableHead>
                    <TableHead className="font-semibold">Service</TableHead>
                    <TableHead className="font-semibold">Contact Person</TableHead>
                    <TableHead className="font-semibold">Created</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client, index) => (
                    <TableRow 
                      key={client.client_id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      data-testid={`client-row-${client.client_id}`}
                    >
                      <TableCell className="font-medium text-slate-500">
                        {index + 1}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-indigo-600" />
                          </div>
                          {client.client_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        {client.city && (
                          <span className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                            <MapPin className="w-3 h-3" />
                            {client.city}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {client.service_taken && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                            {client.service_taken}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{client.contact_person_name || "-"}</TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {formatDate(client.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewProfile(client)}
                            data-testid={`view-client-${client.client_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canManageClients && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(client)}
                                data-testid={`edit-client-${client.client_id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete(client)}
                                data-testid={`delete-client-${client.client_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Client Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Add New Client
            </DialogTitle>
            <DialogDescription>
              Enter the client details below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add_client_name">Client Name *</Label>
                <Input
                  id="add_client_name"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  placeholder="Enter client name"
                  data-testid="client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add_city">City</Label>
                <Input
                  id="add_city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  data-testid="client-city-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add_service_taken">Service Taken</Label>
              <Input
                id="add_service_taken"
                name="service_taken"
                value={formData.service_taken}
                onChange={handleInputChange}
                placeholder="e.g., SEO, Web Development, Marketing"
                data-testid="client-service-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add_contact_person_name">Contact Person</Label>
                <Input
                  id="add_contact_person_name"
                  name="contact_person_name"
                  value={formData.contact_person_name}
                  onChange={handleInputChange}
                  placeholder="Contact person name"
                  data-testid="client-contact-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add_contact_phone">Phone</Label>
                <Input
                  id="add_contact_phone"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleInputChange}
                  placeholder="Phone number"
                  data-testid="client-phone-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add_contact_email">Email</Label>
              <Input
                id="add_contact_email"
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={handleInputChange}
                placeholder="Email address"
                data-testid="client-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add_notes">Notes</Label>
              <Textarea
                id="add_notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes about the client"
                rows={3}
                data-testid="client-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateClient}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-client-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-indigo-600" />
              Edit Client
            </DialogTitle>
            <DialogDescription>
              Update the client details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_client_name">Client Name *</Label>
                <Input
                  id="edit_client_name"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  placeholder="Enter client name"
                  data-testid="edit-client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_city">City</Label>
                <Input
                  id="edit_city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  data-testid="edit-client-city-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_service_taken">Service Taken</Label>
              <Input
                id="edit_service_taken"
                name="service_taken"
                value={formData.service_taken}
                onChange={handleInputChange}
                placeholder="e.g., SEO, Web Development, Marketing"
                data-testid="edit-client-service-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_contact_person_name">Contact Person</Label>
                <Input
                  id="edit_contact_person_name"
                  name="contact_person_name"
                  value={formData.contact_person_name}
                  onChange={handleInputChange}
                  placeholder="Contact person name"
                  data-testid="edit-client-contact-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_contact_phone">Phone</Label>
                <Input
                  id="edit_contact_phone"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleInputChange}
                  placeholder="Phone number"
                  data-testid="edit-client-phone-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_contact_email">Email</Label>
              <Input
                id="edit_contact_email"
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={handleInputChange}
                placeholder="Email address"
                data-testid="edit-client-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes about the client"
                rows={3}
                data-testid="edit-client-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateClient}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="update-client-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Client
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedClient?.client_name}</strong>? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteClient}
              data-testid="confirm-delete-client-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={(open) => {
        setShowProfileDialog(open);
        if (!open) {
          navigate("/work-hub/clients");
        }
      }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <div className="text-xl">{selectedClient?.client_name}</div>
                <div className="text-sm font-normal text-slate-500">Client Profile</div>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <div className="space-y-6 py-4">
              {/* Client Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wide">City</div>
                    <div className="font-medium">{selectedClient.city || "Not specified"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <Briefcase className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wide">Service Taken</div>
                    <div className="font-medium">{selectedClient.service_taken || "Not specified"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <User className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wide">Contact Person</div>
                    <div className="font-medium">{selectedClient.contact_person_name || "Not specified"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wide">Created Date</div>
                    <div className="font-medium">{formatDate(selectedClient.created_at)}</div>
                  </div>
                </div>
                {selectedClient.contact_email && (
                  <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500 uppercase tracking-wide">Email</div>
                      <div className="font-medium">{selectedClient.contact_email}</div>
                    </div>
                  </div>
                )}
                {selectedClient.contact_phone && (
                  <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500 uppercase tracking-wide">Phone</div>
                      <div className="font-medium">{selectedClient.contact_phone}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              {selectedClient.notes && (
                <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-500 uppercase tracking-wide">Notes</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{selectedClient.notes}</p>
                </div>
              )}

              {/* Task History */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Task History (Completed)
                </h3>
                
                {loadingTasks ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  </div>
                ) : clientTasks.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No completed tasks for this client</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {clientTasks.map((task) => (
                      <div 
                        key={task.task_id}
                        className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <div>
                            <div className="font-medium text-sm">{task.title}</div>
                            <div className="text-xs text-slate-500">
                              {task.assigned_user_names?.join(", ") || "Unassigned"}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-slate-500">Due: {formatDate(task.due_date)}</div>
                          <div className="text-xs text-green-600">Completed: {formatDate(task.updated_at || task.created_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowProfileDialog(false);
              navigate("/work-hub/clients");
            }}>
              Close
            </Button>
            {canManageClients && selectedClient && (
              <Button 
                onClick={() => {
                  setShowProfileDialog(false);
                  handleEdit(selectedClient);
                }}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Client
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
